import {
  memberViewSchema,
  type MemberLevel,
  type MemberOrigin,
  type MemberView,
} from '@growup/accounts-shared';
import { accountStreamerSchema } from '@growup/streaming-shared';
import {
  Autocomplete,
  Button,
  Card,
  Field,
  Notice,
  Select,
  StatusBadge,
  Table,
  type Column,
} from '@growup/ui';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf } from '../api';
import { LEVEL_LOOK, ORIGIN_LABEL, shortName } from './labels';
import { LevelBadge } from './LevelBadge';
import { MemberCardGrid } from './MemberCardGrid';
import { MemberDetail } from './MemberDetail';
import { shirtDue, StreamerShirtBanner, useStreamerGifts } from './StreamerShirt';

const PAGE = 50;

type Layout = 'grelha' | 'tabela';
const LAYOUT_KEY = 'gu.socios.vista';

/** Grelha de cartas ou tabela: cada pessoa do staff fica com a sua escolha. */
function useLayout(): [Layout, (next: Layout) => void] {
  const [layout, setLayout] = useState<Layout>(() => {
    try {
      return localStorage.getItem(LAYOUT_KEY) === 'tabela' ? 'tabela' : 'grelha';
    } catch {
      return 'grelha';
    }
  });
  return [
    layout,
    (next) => {
      setLayout(next);
      try {
        localStorage.setItem(LAYOUT_KEY, next);
      } catch {
        // Sem armazenamento (janela privada): fica so ate sair.
      }
    },
  ];
}

/** O botao Grelha | Tabela. */
export function LayoutToggle({
  value,
  onChange,
}: {
  value: Layout;
  onChange: (next: Layout) => void;
}) {
  return (
    <div role="group" aria-label="Vista" className="flex gap-1">
      {(
        [
          ['grelha', 'Grelha'],
          ['tabela', 'Tabela'],
        ] as const
      ).map(([id, label]) => (
        <Button
          key={id}
          size="sm"
          variant={value === id ? 'primary' : 'secondary'}
          aria-pressed={value === id}
          onClick={() => onChange(id)}
        >
          {label}
        </Button>
      ))}
    </div>
  );
}

/** The short name (when readable), then login, origin and what is missing. */
export function PersonCell({ member }: { member: MemberView }) {
  const fullName = member.profile?.fullName;
  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate font-semibold" title={fullName}>
        {fullName ? shortName(fullName) : member.login}
      </span>
      <span className="truncate text-caption text-gu-ink-muted">
        {member.profile ? `${member.login} · ` : ''}
        {ORIGIN_LABEL[member.origin]}
        {member.redacted ? ' · dados reservados' : ''}
        {!member.redacted && member.profile === null ? ' · sem ficha' : ''}
      </span>
    </span>
  );
}

/** Sugestao do campo de pesquisa: a mesma pessoa que aparece na lista. */
export function MemberSuggestion({ member }: { member: MemberView }) {
  return (
    <span className="flex items-center justify-between gap-3">
      <PersonCell member={member} />
      <LevelBadge level={member.level} />
    </span>
  );
}

/** As primeiras pessoas para `q`, com as mesmas regras de privacidade da lista. */
export function searchMembers(q: string): Promise<MemberView[]> {
  const params = new URLSearchParams({ q, limit: '8' });
  return getJson(`/api/members/members?${params.toString()}`, z.array(memberViewSchema));
}

function columns(
  streamers: ReadonlySet<string>,
  shirts: ReadonlySet<string>,
): Column<MemberView>[] {
  return [
    { key: 'person', header: 'Pessoa', cell: (m) => <PersonCell member={m} />, fill: true },
    {
      key: 'level',
      header: 'Nível',
      align: 'right',
      cell: (m) => (
        <span className="inline-flex flex-wrap justify-end gap-1">
          {shirts.has(m.accountId) ? (
            <StatusBadge tone="warning" symbol="★">
              1 ano de streamer
            </StatusBadge>
          ) : null}
          {streamers.has(m.accountId) ? (
            <StatusBadge tone="info" symbol="▶">
              Streamer
            </StatusBadge>
          ) : null}
          <LevelBadge level={m.level} />
        </span>
      ),
    },
  ];
}

/**
 * Who is a streamer now (active or paused), for the tag in the list. A help, not
 * a must: if the Stream Portal fails, the list shows without it.
 */
function useStreamerAccounts(): ReadonlySet<string> {
  const [ids, setIds] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    getJson('/api/streaming/streamer-accounts', z.array(accountStreamerSchema))
      .then((rows) => setIds(new Set(rows.map((r) => r.accountId))))
      .catch(() => undefined);
  }, []);
  return ids;
}

/** Search and list members; the selected one opens beside the list. */
export function MembersView({
  canManage,
  canSeeStreamerNumbers = false,
  selectedId,
  onSelect,
  onOpenStreamer,
}: {
  canManage: boolean;
  canSeeStreamerNumbers?: boolean;
  selectedId: string | null;
  onSelect: (accountId: string | null) => void;
  onOpenStreamer?: ((accountId: string) => void) | undefined;
}) {
  const streamers = useStreamerAccounts();
  const gifts = useStreamerGifts(canSeeStreamerNumbers);
  const shirts = new Set(shirtDue(gifts.list).map((s) => s.accountId));
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState<MemberLevel | ''>('');
  const [origin, setOrigin] = useState<MemberOrigin | ''>('');
  const [offset, setOffset] = useState(0);
  const [list, setList] = useState<MemberView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [layout, setLayout] = useLayout();
  const grid = layout === 'grelha';

  useEffect(() => setOffset(0), [query, level, origin]);

  useEffect(() => {
    if (grid) return;
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (query.trim()) params.set('q', query.trim());
    if (level) params.set('level', level);
    if (origin) params.set('origin', origin);
    // Typing should not fire one request per key.
    const timer = setTimeout(() => {
      getJson(`/api/members/members?${params.toString()}`, z.array(memberViewSchema))
        .then((rows) => {
          setError(null);
          setList(rows);
        })
        .catch((e: unknown) => setError(messageOf(e)));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, level, origin, offset, version, grid]);

  const filters = (
    <>
      <Field label="Procurar" hint="Escolha uma sugestão para abrir a ficha.">
        <Autocomplete
          placeholder="Login ou nome"
          value={query}
          onChange={setQuery}
          search={searchMembers}
          getKey={(m) => m.accountId}
          renderItem={(m) => <MemberSuggestion member={m} />}
          onSelect={(m) => onSelect(m.accountId)}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nível">
          <Select value={level} onChange={(e) => setLevel(e.target.value as MemberLevel | '')}>
            <option value="">Todos</option>
            {(Object.keys(LEVEL_LOOK) as MemberLevel[]).map((l) => (
              <option key={l} value={l}>
                {LEVEL_LOOK[l].label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Origem">
          <Select value={origin} onChange={(e) => setOrigin(e.target.value as MemberOrigin | '')}>
            <option value="">Todas</option>
            {(Object.keys(ORIGIN_LABEL) as MemberOrigin[]).map((o) => (
              <option key={o} value={o}>
                {ORIGIN_LABEL[o]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </>
  );

  const detail = selectedId ? (
    <MemberDetail
      key={selectedId}
      accountId={selectedId}
      canManage={canManage}
      canSeeStreamerNumbers={canSeeStreamerNumbers}
      onOpen={onSelect}
      onOpenStreamer={onOpenStreamer}
      onChanged={() => {
        setVersion((v) => v + 1);
        gifts.reload();
      }}
    />
  ) : null;

  if (grid) {
    // Na grelha a ficha ocupa a largura toda; a grelha fica como estava.
    return (
      <div className="flex flex-col gap-4">
        <StreamerShirtBanner list={gifts.list} onOpen={onSelect} onChanged={gifts.reload} />
        {detail ? (
          <>
            <div>
              <Button variant="secondary" size="sm" onClick={() => onSelect(null)}>
                ← Voltar às cartas
              </Button>
            </div>
            {detail}
          </>
        ) : null}
        <div className={detail ? 'hidden' : 'flex flex-col gap-4'}>
          <Card headingLevel={2} title="Membros">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
              <div className="flex flex-1 flex-col gap-3 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-end">
                {filters}
              </div>
              <LayoutToggle value={layout} onChange={setLayout} />
            </div>
            <p className="text-caption text-gu-ink-muted">
              Por antiguidade: quem é membro há mais tempo aparece primeiro.
            </p>
          </Card>
          <MemberCardGrid
            query={query}
            level={level}
            origin={origin}
            version={version}
            streamers={streamers}
            shirts={shirts}
            onOpen={onSelect}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[26rem_minmax(0,1fr)]">
      <StreamerShirtBanner list={gifts.list} onOpen={onSelect} onChanged={gifts.reload} />
      <Card headingLevel={2} title="Membros">
        <div className="flex justify-end">
          <LayoutToggle value={layout} onChange={setLayout} />
        </div>
        {filters}
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <Table
          caption="Membros encontrados"
          columns={columns(streamers, shirts)}
          rows={list ?? []}
          rowKey={(m) => m.accountId}
          empty={list ? 'Ninguém encontrado.' : 'A carregar…'}
          onRowClick={(m) => onSelect(m.accountId)}
        />
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            Anteriores
          </Button>
          <span className="text-caption text-gu-ink-muted">
            {list && list.length > 0 ? `${String(offset + 1)}–${String(offset + list.length)}` : ''}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={!list || list.length < PAGE}
            onClick={() => setOffset(offset + PAGE)}
          >
            Seguintes
          </Button>
        </div>
      </Card>
      {detail ?? <p className="text-gu-ink-muted">Escolha alguém da lista para ver a ficha.</p>}
    </div>
  );
}
